import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import type { PriceRun, RrpWritten } from "./api";
import { channelLabel, COPY, humanDate, money, personName, stamp } from "./format";
import { OVERRIDE_COPY } from "./overrides";
import { placeName } from "./price";
import { rrpsWrittenText } from "./rrp";

/**
 * The list to apply by hand after a commit: one row per committed line, a tick box the operator uses for their own
 * tracking (kept nowhere; C6 will record the applied state), and Done to dismiss. The next daily calculation shows
 * the committed prices as live. C1 v1.42: under the title, the RRPs this commit wrote (C2 v1.43 `rrps`), one item per
 * product, so the person sees which RRPs now follow the prices they just committed; nothing when it wrote none. C1 v1.50: a line
 * committed as a temporary price names its until date and the price it returns to the morning after (C2 v1.52), so the person
 * applying it knows a return follows; the return itself is listed under Price overrides when it is written.
 */
export function ApplyPanel({ committed, rrps = [], onDone }: { committed: PriceRun[]; rrps?: RrpWritten[]; onDone: () => void }) {
  const lines = committed.flatMap((run) => run.lines);
  const rrpLine = rrpsWrittenText(rrps, lines);
  const [done, setDone] = useState<ReadonlySet<number>>(new Set());
  const first = committed[0];
  const toggle = (id: number) => setDone((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  return (
    <section role="region" aria-label={COPY.applyRegion} className="rounded-md border border-ks-purple bg-ks-purple-4 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ks-ink"><b>{COPY.committedTitle(lines.length, stamp(first?.committed_at), personName(first?.committed_by))}</b> {COPY.applyNote}</p>
        <Button type="button" size="sm" variant="outline" onClick={onDone}>{COPY.done}</Button>
      </div>
      {rrpLine && <p className="text-sm text-ks-ink" data-rrps-written>{rrpLine}</p>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead><span className="sr-only">{COPY.applied}</span></TableHead>
            <TableHead>Channel</TableHead><TableHead>Where</TableHead><TableHead>Seller SKU</TableHead><TableHead>Product</TableHead><TableHead className="text-right">Price</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.id} className={done.has(line.id) ? "text-ks-grey-1" : undefined}>
              <TableCell><Checkbox aria-label={COPY.applied} checked={done.has(line.id)} onCheckedChange={() => toggle(line.id)} /></TableCell>
              <TableCell>{channelLabel(line)}</TableCell>
              <TableCell>{placeName(line)}</TableCell>
              <TableCell className="font-mono text-xs">{line.amazon_sku ?? line.xero_sku}</TableCell>
              <TableCell>{line.product} {line.variant}</TableCell>
              <TableCell className="text-right tabular-nums">
                {line.old_price == null ? COPY.none : money(line.old_price, line.currency)} → <b>{money(line.new_price, line.currency)}</b> {line.currency}
                {line.floor_override_reason && <div className="text-xs text-ks-grey-1">{COPY.overrideNote(line.floor_override_reason)}</div>}
                {line.override_until && <div className="text-xs text-ks-purple" data-apply-override>{OVERRIDE_COPY.applyNote(humanDate(line.override_until), line.old_price == null ? null : money(line.old_price, line.currency))}</div>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}
